import { IonCheckbox, IonContent, IonItem, IonLabel, IonList, IonModal } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

interface QuestionData {
    sentence: string;
    correctOption: string[]; // Update to handle multiple correct nouns
}


const DescribingWords: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]); // Store multiple selected nouns
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const [words, setWords] = useState<string[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);


    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'DescribingWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/DescribingWords.json');
                data = module.default;
            }


            const shuffledData = shuffleArray(data);
            setQuestions(shuffledData);
            const myQuestion = (shuffledData[currentIndex]?.sentence.split("."));
            setWords(myQuestion[0].split(" ") || []);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };


    useEffect(() => {
        fetchQuestions();
        document.title = "Describing Words Worksheets for UKG | Learn Adjectives with Fun Activities"; // Set page title
    }, []);

    const handleOptionClick = (option: string) => {
        // Toggle the option in the selectedOptions array
        const updatedSelections = selectedOptions.includes(option)
            ? selectedOptions.filter((item) => item !== option) // Remove if already selected
            : [...selectedOptions, option]; // Add if not selected

        console.log(updatedSelections)

        setSelectedOptions(updatedSelections);

        const correctAnswers = questions[currentIndex]?.correctOption || [];

        // Check if all correct answers are selected
        const isCorrect =
            correctAnswers.every((noun) => updatedSelections.includes(noun)) &&
            updatedSelections.length === correctAnswers.length;

        if (isCorrect) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    const myQuestion = (questions[nextIndex]?.sentence.split("."));
                    setWords(myQuestion[0].split(" ") || []);
                    setSelectedOptions([]); // Reset selections
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setError(false);
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setError(false); // Reset error as user continues selecting
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach your UKG child about describing words (adjectives) with engaging and easy-to-understand activities at Mama’s Guidance, helping them build a strong vocabulary and language foundation.' />
            </Helmet>
            <div className="contentPart showCenter commonNoun additionWord" style={{ marginTop: "0%" }}>
                <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                <IonModal
                    ref={modal}
                    trigger="open-modal"
                    initialBreakpoint={1}
                    breakpoints={[0, 0.5, 0.75, 1]}
                    handleBehavior="cycle"
                    backdropDismiss={false}  // ✅ Prevents click-to-close
                    className="full-page-modal"
                >
                    <IonContent className="ion-padding">
                        <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                        <h1>Describing Words</h1>

                        <p>
                            <strong>Describing words</strong>, also known as <strong>adjectives</strong>, help children describe how someone or something looks, feels, or behaves. At the UKG level, kids begin to use simple words to describe <b><em>people</em>, <em>places</em>, <em>animals</em>, and <em>things</em></b>. These worksheets make learning adjectives interactive and engaging.
                        </p>

                        <h2>📝 What Are Describing Words?</h2>
                        <p>
                            Describing words tell us more about a noun. They answer questions like <strong>What kind?</strong>, <strong>How many?</strong>, or <strong>What color?</strong>. For example: <b><em>tall boy, red apple, soft pillow</em></b>.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Understand the concept of describing words (adjectives)</li>
                            <li>Identify adjectives in simple sentences</li>
                            <li>Use describing words correctly with nouns</li>
                            <li>Improve vocabulary and sentence construction</li>
                        </ul>

                        <h2>🖍️ Activities in the Worksheets</h2>
                        <ul>
                            <li>Circle the describing words in a sentence</li>
                            <li>Match pictures with the correct adjectives</li>
                            <li>Fill in the blanks using describing words</li>
                            <li>Choose the best adjective from a list</li>
                            <li>Color objects based on description (<strong>e.g., Color the big ball red</strong>)</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Use toys or real objects to talk about size, color, or shape</li>
                            <li>Read storybooks and highlight describing words while reading</li>
                            <li>Ask questions like: <strong>“Is the cat big or small?” or “What color is the balloon?”</strong></li>
                            <li>Encourage kids to use one describing word in every sentence</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/english-language/articles"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Articles</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/english-language/one-and-many"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>One and Many</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/english-language/rhyming-words"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Rhyming Words</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            While talking at home, describe things around you. Say, “Look at the <strong>bright sun</strong>” or “That’s a <strong>cold drink</strong>.” Let your child repeat and use those words.
                        </p>

                        <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                    </IonContent>
                </IonModal>
                <article className="visually-hidden">
                    <h1>Describing Words</h1>

                    <p>
                        <strong>Describing words</strong>, also known as <strong>adjectives</strong>, help children describe how someone or something looks, feels, or behaves. At the UKG level, kids begin to use simple words to describe <b><em>people</em>, <em>places</em>, <em>animals</em>, and <em>things</em></b>. These worksheets make learning adjectives interactive and engaging.
                    </p>

                    <h2>📝 What Are Describing Words?</h2>
                    <p>
                        Describing words tell us more about a noun. They answer questions like <strong>What kind?</strong>, <strong>How many?</strong>, or <strong>What color?</strong>. For example: <b><em>tall boy, red apple, soft pillow</em></b>.
                    </p>

                    <h2>🎯 Learning Objectives</h2>
                    <ul>
                        <li>Understand the concept of describing words (adjectives)</li>
                        <li>Identify adjectives in simple sentences</li>
                        <li>Use describing words correctly with nouns</li>
                        <li>Improve vocabulary and sentence construction</li>
                    </ul>

                    <h2>🖍️ Activities in the Worksheets</h2>
                    <ul>
                        <li>Circle the describing words in a sentence</li>
                        <li>Match pictures with the correct adjectives</li>
                        <li>Fill in the blanks using describing words</li>
                        <li>Choose the best adjective from a list</li>
                        <li>Color objects based on description (<strong>e.g., Color the big ball red</strong>)</li>
                    </ul>

                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                    <ul>
                        <li>Use toys or real objects to talk about size, color, or shape</li>
                        <li>Read storybooks and highlight describing words while reading</li>
                        <li>Ask questions like: <strong>“Is the cat big or small?” or “What color is the balloon?”</strong></li>
                        <li>Encourage kids to use one describing word in every sentence</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/ukg/english-language/articles"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Articles</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/english-language/one-and-many"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>One and Many</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/english-language/rhyming-words"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Rhyming Words</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🌟 Mama’s Guidance Tip</h2>
                    <p>
                        While talking at home, describe things around you. Say, “Look at the <strong>bright sun</strong>” or “That’s a <strong>cold drink</strong>.” Let your child repeat and use those words.
                    </p>
                </article>

                {showImage === false ? (
                    <>
                        <h2 className="mt-2 mb-2" style={{ fontWeight: "normal" }}>
                            Q. Choose all the describing words below -
                        </h2>
                        <h2 className="mt-2 mb-2">{questions[currentIndex]?.sentence}</h2>
                        <div className="mt-2 grid-container" style={{ marginBottom: "20px" }}>
                            <div className="grid-box optionDiv" style={{ display: "block" }}>
                                <IonList className="grid-item-container">
                                    {words.map((option, index) => (
                                        <IonItem
                                            className={`grid-item ${selectedOptions.includes(option)
                                                ? questions[currentIndex]?.correctOption.includes(option)
                                                    ? "correct"
                                                    : "errorOptions"
                                                : ""
                                                }`}
                                            key={index}
                                        >
                                            <IonCheckbox
                                                slot="start"
                                                checked={selectedOptions.includes(option)}
                                                onIonChange={() => handleOptionClick(option)}
                                            />
                                            <IonLabel>{option}</IonLabel>
                                        </IonItem>
                                    ))}
                                </IonList>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="contentPart showMiddle">
                            <img
                                style={{ height: "150px" }}
                                className="img-fluid"
                                src={process.env.REACT_APP_WELDONE_LINK}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default DescribingWords;
